import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, ShieldCheck, User as UserIcon, ArrowLeft, Trash2, KeyRound, Copy, X, Eye, EyeOff, Pencil, CheckCircle2, Clock3 } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";

function maskEmail(email) {
  if (!email) return "";
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const visible = local.slice(0, 2);
  const masked = "*".repeat(Math.max(2, local.length - 2));
  return `${visible}${masked}@${domain}`;
}

const ROLE_LABEL = { user: "Utente", admin: "Admin", superadmin: "Superadmin" };
const ROLE_STYLE = {
  user: "bg-[#F5F1EA] dark:bg-[#1A1A1A] text-[#4A4742] dark:text-[#C8C2BA]",
  admin: "bg-[#DDE7DC] dark:bg-[#1A2E20] text-[#2B593F] dark:text-[#8FC79F]",
  superadmin: "bg-[#F4DAD3] dark:bg-[#2E1A18] text-[#A6432E] dark:text-[#F08A78]",
};
const ROLE_ICON = { user: UserIcon, admin: Shield, superadmin: ShieldCheck };

export default function AdminPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);

  useEffect(() => { document.title = "Tavoli da Gioco — Pannello Admin"; }, []);
  const [revealedEmails, setRevealedEmails] = useState(new Set());
  const [editName, setEditName] = useState(null);
  const [editNameValue, setEditNameValue] = useState("");

  const isSuper = user?.role === "superadmin";
  const isAdmin = user?.role === "admin" || isSuper;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/" replace />;

  const approveUser = async (u) => {
    setBusyId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/approve`);
      toast.success(`Utente approvato: ${u.email}`);
      window.dispatchEvent(new CustomEvent("user-approved"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const setRole = async (u, role) => {
    setBusyId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      toast.success(`Ruolo aggiornato: ${u.email} → ${ROLE_LABEL[role]}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Eliminare l'utente "${u.email}"?\nTutti i tavoli creati da questo utente verranno eliminati.`)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      const tables = data?.deleted_tables ?? 0;
      toast.success(`Utente eliminato${tables ? ` (${tables} tavoli rimossi)` : ""}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const generateResetLink = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-link`);
      setResetInfo({ url: data.reset_url, email: data.user_email, expiresAt: data.expires_at });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleEmail = (userId) => {
    setRevealedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const openEditName = (u) => {
    setEditName({ userId: u.id });
    setEditNameValue(u.name);
  };

  const saveEditName = async () => {
    if (!editName) return;
    setBusyId(editName.userId);
    try {
      await api.patch(`/admin/users/${editName.userId}/name`, { name: editNameValue });
      toast.success("Nome aggiornato");
      setEditName(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const copyResetLink = async () => {
    if (!resetInfo) return;
    try {
      await navigator.clipboard.writeText(resetInfo.url);
      toast.success("Link copiato negli appunti");
    } catch (e) {
      toast.error("Impossibile copiare automaticamente. Selezionalo manualmente.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-[#181818]">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-2 text-sm text-[#2B593F] dark:text-[#8FC79F] hover:underline mb-6"
        >
          <ArrowLeft size={14} /> Torna al dashboard
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#C8553D] text-[#FDFBF7] flex items-center justify-center">
            <ShieldCheck size={20} strokeWidth={1.8} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] tracking-tight">Pannello Admin</h1>
            <p className="text-sm text-[#757169] dark:text-[#B8B2AA]">
              {isSuper
                ? "Gestisci i ruoli degli utenti. Solo i superadmin possono promuovere altri utenti ad admin."
                : "Visualizza gli utenti della piattaforma."}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl mt-8 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#EAE3D9] dark:border-[#2A2A2A] bg-[#F5F1EA]/50 dark:bg-[#1A1A1A] text-[10px] uppercase tracking-[0.15em] text-[#757169] dark:text-[#B8B2AA] font-semibold">
            <div className="col-span-4">Utente</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Ruolo</div>
            <div className="col-span-3 text-right">Azioni</div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-sm text-[#757169] dark:text-[#B8B2AA]">Caricamento...</div>
          ) : users.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#757169] dark:text-[#B8B2AA]">Nessun utente.</div>
          ) : (
            users.map((u) => {
              const Icon = ROLE_ICON[u.role] || UserIcon;
              const isSelf = u.id === user.id;
              const isTargetSuper = u.role === "superadmin";
              const canManage = isSuper && !isSelf && !isTargetSuper;
              const canApprove = isAdmin && !isSelf && !isTargetSuper;
              return (
                <div
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="border-b border-[#EAE3D9] dark:border-[#2A2A2A] last:border-b-0 px-4 sm:px-6 py-4
                             flex flex-col gap-3
                             md:grid md:grid-cols-12 md:gap-4 md:items-center md:py-4"
                >
                  {/* Identity */}
                  <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#F5F1EA] dark:bg-[#1A1A1A] flex items-center justify-center text-[#2B593F] dark:text-[#8FC79F] flex-shrink-0">
                      <Icon size={16} strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-[#2D2A26] dark:text-[#F5F1EA] truncate">{u.name}</p>
                        {!u.approved && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#FEF3C7] dark:bg-[#2E2510] text-[#92400E] dark:text-[#F5C842] px-2 py-0.5 rounded-full flex-shrink-0">
                            <Clock3 size={10} /> Attesa
                          </span>
                        )}
                      </div>
                      {u.username && (
                        <p className="text-[11px] text-[#B0A898] dark:text-[#4A4742] font-mono truncate">@{u.username}</p>
                      )}
                      <p data-ph-no-capture className="md:hidden text-xs text-[#757169] dark:text-[#B8B2AA] truncate font-mono">
                        {revealedEmails.has(u.id) ? u.email : maskEmail(u.email)}
                      </p>
                    </div>
                    <span className={`md:hidden ${ROLE_STYLE[u.role] || ROLE_STYLE.user} px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider flex-shrink-0`}>
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </div>

                  <div className="hidden md:flex md:col-span-3 items-center gap-1.5 min-w-0">
                    <span data-ph-no-capture className="text-sm text-[#4A4742] dark:text-[#C8C2BA] truncate font-mono">
                      {revealedEmails.has(u.id) ? u.email : maskEmail(u.email)}
                    </span>
                    <button
                      onClick={() => toggleEmail(u.id)}
                      className="text-[#757169] hover:text-[#2B593F] dark:hover:text-[#8FC79F] flex-shrink-0 p-1 rounded"
                      title={revealedEmails.has(u.id) ? "Nascondi email" : "Mostra email"}
                    >
                      {revealedEmails.has(u.id) ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                  </div>

                  <div className="hidden md:block md:col-span-2">
                    <span className={`${ROLE_STYLE[u.role] || ROLE_STYLE.user} px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider`}>
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </div>

                  <div className="md:col-span-3 flex justify-start md:justify-end items-center gap-2">
                    {canManage ? (
                      <>
                        <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={busyId === u.id}>
                          <SelectTrigger
                            data-testid={`role-select-${u.id}`}
                            className="h-9 w-full md:w-[120px] rounded-xl border-[#EAE3D9] dark:border-[#2A2A2A] bg-[#FDFBF7] dark:bg-[#1A1A1A] dark:text-[#F5F1EA]"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl">
                            <SelectItem value="user" data-testid={`role-option-user-${u.id}`}>Utente</SelectItem>
                            <SelectItem value="admin" data-testid={`role-option-admin-${u.id}`}>Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        <button
                          onClick={() => openEditName(u)}
                          disabled={busyId === u.id}
                          data-testid={`edit-name-${u.id}`}
                          className="text-[#757169] hover:text-[#2B593F] dark:hover:text-[#8FC79F] hover:bg-[#F5F1EA] dark:hover:bg-[#1E1E1E] p-2 rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
                          title="Modifica nome"
                        >
                          <Pencil size={16} />
                        </button>
                        {!u.approved && (
                          <button
                            onClick={() => approveUser(u)}
                            disabled={busyId === u.id}
                            data-testid={`approve-user-${u.id}`}
                            className="text-[#2B593F] dark:text-[#8FC79F] hover:bg-[#DDE7DC] dark:hover:bg-[#1A2E20] p-2 rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
                            title="Approva registrazione"
                          >
                            <CheckCircle2 size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => generateResetLink(u)}
                          disabled={busyId === u.id}
                          data-testid={`reset-link-${u.id}`}
                          className="text-[#757169] hover:text-[#2B593F] dark:hover:text-[#8FC79F] hover:bg-[#F5F1EA] dark:hover:bg-[#1E1E1E] p-2 rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
                          title="Genera link reset password"
                        >
                          <KeyRound size={16} />
                        </button>
                        <button
                          onClick={() => deleteUser(u)}
                          disabled={busyId === u.id}
                          data-testid={`delete-user-${u.id}`}
                          className="text-[#757169] hover:text-[#C8553D] hover:bg-[#F4DAD3]/40 dark:hover:bg-[#C8553D]/10 p-2 rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
                          title="Elimina utente"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    ) : (canApprove && !u.approved) ? (
                        <button
                          onClick={() => approveUser(u)}
                          disabled={busyId === u.id}
                          data-testid={`approve-user-${u.id}`}
                          className="text-[#2B593F] dark:text-[#8FC79F] hover:bg-[#DDE7DC] dark:hover:bg-[#1A2E20] p-2 rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
                          title="Approva registrazione"
                        >
                          <CheckCircle2 size={16} />
                        </button>
                    ) : (
                      <span className="text-xs text-[#757169] dark:text-[#B8B2AA] italic">
                        {isSelf ? "Tu" : isTargetSuper ? "Protetto" : "—"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      {editName && (
        <div
          className="fixed inset-0 bg-[#2D2A26]/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
          onClick={() => setEditName(null)}
        >
          <div
            className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-3xl p-6 sm:p-8 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl font-semibold text-[#2D2A26] dark:text-[#F5F1EA] mb-4">Modifica nome</h3>
            <input
              className="w-full border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-xl px-4 py-2.5 text-sm bg-[#FDFBF7] dark:bg-[#1A1A1A] dark:text-[#F5F1EA] focus:outline-none focus:border-[#2B593F]"
              value={editNameValue}
              onChange={(e) => setEditNameValue(e.target.value)}
              maxLength={80}
              autoFocus
            />
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setEditName(null)}
                className="flex-1 h-11 rounded-xl text-[#2D2A26] dark:text-[#F5F1EA] hover:bg-[#F5F1EA] dark:hover:bg-[#1A1A1A] font-medium"
              >
                Annulla
              </button>
              <button
                onClick={saveEditName}
                disabled={!editNameValue.trim() || busyId === editName.userId}
                className="flex-1 h-11 rounded-xl bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] font-medium disabled:opacity-50"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {resetInfo && (
        <div
          className="fixed inset-0 bg-[#2D2A26]/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
          onClick={() => setResetInfo(null)}
        >
          <div
            className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-3xl p-6 sm:p-8 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="reset-link-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#C8553D] text-[#FDFBF7] flex items-center justify-center">
                  <KeyRound size={20} strokeWidth={1.8} />
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold text-[#2D2A26] dark:text-[#F5F1EA]">Link reset password</h3>
                  <p className="text-xs text-[#757169] dark:text-[#B8B2AA]">Per: {resetInfo.email}</p>
                </div>
              </div>
              <button
                onClick={() => setResetInfo(null)}
                data-testid="close-reset-modal"
                className="text-[#757169] hover:bg-[#F5F1EA] dark:hover:bg-[#1A1A1A] p-2 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-sm text-[#4A4742] dark:text-[#C8C2BA] leading-relaxed mb-4">
              Condividi questo link con l'utente (WhatsApp / Telegram / di persona). È valido per <strong>1 ora</strong> e può essere usato <strong>una sola volta</strong>.
            </p>

            <div className="bg-[#F5F1EA] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-xl p-3 break-all text-xs text-[#2D2A26] dark:text-[#F5F1EA] font-mono select-all" data-testid="reset-link-text">
              {resetInfo.url}
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setResetInfo(null)}
                className="flex-1 h-11 rounded-xl text-[#2D2A26] dark:text-[#F5F1EA] hover:bg-[#F5F1EA] dark:hover:bg-[#1A1A1A] font-medium"
                data-testid="reset-modal-close"
              >
                Chiudi
              </button>
              <button
                onClick={copyResetLink}
                data-testid="reset-modal-copy"
                className="flex-1 h-11 rounded-xl bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] font-medium inline-flex items-center justify-center gap-2"
              >
                <Copy size={16} /> Copia link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
